import React, { useState, useEffect, useCallback, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import RefreshIcon from "@material-ui/icons/Refresh";
import Paper from "@material-ui/core/Paper";
import { format, isToday, isYesterday } from "date-fns";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { useConfirm } from "../../components/ConfirmDialog";
import { connectToSocket } from "../../services/socket";
import { i18n } from "../../translate/i18n";
import JsonTree from "../../components/JsonTree";

const useStyles = makeStyles(theme => ({
  page: {
    padding: theme.spacing(3),
    height: "100%",
    display: "flex",
    flexDirection: "column",
    background: theme.app.chatBg,
    [theme.breakpoints.down("sm")]: { height: "auto", minHeight: "100%" },
  },
  header: { display: "flex", alignItems: "center", gap: 10, marginBottom: theme.spacing(2) },
  title: { fontFamily: theme.app.heading, fontWeight: 700, fontSize: 22, color: theme.app.ink },
  titleCount: { fontFamily: theme.app.mono, fontSize: 12, color: theme.app.textTertiary },
  grow: { flexGrow: 1 },
  body: {
    display: "flex",
    flex: 1,
    minHeight: 0,
    gap: theme.spacing(2),
    [theme.breakpoints.down("sm")]: { flexDirection: "column", minHeight: 0 },
  },
  list: {
    width: 340,
    flexShrink: 0,
    borderRadius: 10,
    border: `1px solid ${theme.app.border}`,
    background: "#fff",
    overflowY: "auto",
    [theme.breakpoints.down("sm")]: { width: "100%", maxHeight: "45vh" },
  },
  dayHead: {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    color: theme.app.textTertiary,
    padding: theme.spacing(1.5, 2, 0.5),
  },
  item: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: theme.spacing(1, 2),
    minHeight: 44,
    cursor: "pointer",
    borderLeft: "2.5px solid transparent",
    "&:hover": { background: theme.app.hover },
  },
  itemActive: { background: "#F0F2F5", borderLeft: `2.5px solid ${theme.app.green}` },
  dot: { width: 7, height: 7, borderRadius: "50%", flexShrink: 0 },
  dotOk: { background: theme.app.ok },
  dotErr: { background: theme.app.danger },
  dotInfo: { background: theme.app.textTertiary },
  itemMsg: { flex: 1, fontSize: 13, color: theme.app.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  itemTime: { fontFamily: theme.app.mono, fontSize: 11, color: theme.app.textTertiary },
  detail: {
    flex: 1,
    minWidth: 0,
    borderRadius: 10,
    border: `1px solid ${theme.app.border}`,
    background: "#fff",
    overflowY: "auto",
    padding: theme.spacing(2),
    [theme.breakpoints.down("sm")]: { width: "100%", minHeight: 280 },
  },
  card: { border: `1px solid ${theme.app.lineSoft}`, borderRadius: 8, marginBottom: 12, padding: 12 },
  cardHead: { display: "flex", alignItems: "center", gap: 8, marginBottom: 6 },
  cardNode: { fontWeight: 600, fontSize: 13, color: theme.app.ink },
  cardTime: { marginLeft: "auto", fontFamily: theme.app.mono, fontSize: 11, color: theme.app.textTertiary },
  metaLabel: { fontSize: 11, color: theme.app.textTertiary, textTransform: "uppercase", letterSpacing: "0.05em" },
  pre: {
    margin: "4px 0 0", padding: "8px 10px", background: theme.app.chip || "#F4F6F8", borderRadius: 6,
    fontSize: 12.5, lineHeight: 1.5, color: theme.app.ink2 || theme.app.textSecondary,
    whiteSpace: "pre-wrap", wordBreak: "break-word", fontFamily: theme.app.body || "inherit",
    maxHeight: 260, overflowY: "auto",
  },
  empty: { padding: theme.spacing(5), textAlign: "center", color: theme.app.textTertiary, fontSize: 13 },
}));

const dayLabel = d => {
  const date = new Date(d);
  if (isToday(date)) return i18n.t("events.today");
  if (isYesterday(date)) return i18n.t("events.yesterday");
  return format(date, "dd/MM/yyyy");
};

const Events = () => {
  const { confirm } = useConfirm();
  const classes = useStyles();
  const [runs, setRuns] = useState([]); // uma execução por linha
  const [selectedRunId, setSelectedRunId] = useState(null);
  const [chain, setChain] = useState([]); // eventos (nós) da execução selecionada
  const [loading, setLoading] = useState(false);

  const fetchRuns = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/events/runs");
      setRuns(data.runs || []);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  // re-carrega a cadeia (detalhe) de um run — usado ao selecionar E ao chegar
  // evento novo em tempo real do run que está aberto (ex.: o evento de FIM).
  const selectedRunIdRef = useRef(null);
  const fetchChain = useCallback(async (runId, silent) => {
    if (!runId) return;
    try {
      const { data } = await api.get("/events", { params: { runId } });
      setChain(data.events || []);
    } catch (err) {
      if (!silent) toastError(err);
    }
  }, []);

  const selectRun = run => {
    setSelectedRunId(run.runId);
    selectedRunIdRef.current = run.runId;
    fetchChain(run.runId);
  };

  const refreshAll = useCallback(() => {
    fetchRuns();
    if (selectedRunIdRef.current) fetchChain(selectedRunIdRef.current, true);
  }, [fetchRuns, fetchChain]);

  useEffect(() => {
    fetchRuns();
  }, [fetchRuns]);

  useEffect(() => {
    const socket = connectToSocket();
    socket.emit("joinRoom", "events");
    socket.on("event", payload => {
      // tempo real: novo evento → atualiza a lista E o detalhe aberto (o de FIM
      // chega depois do de início do run que você já está vendo).
      if (payload.action === "create" || payload.action === "clear") refreshAll();
    });
    return () => {
      socket.emit("leaveRoom", "events");
      socket.disconnect();
    };
  }, [refreshAll]);

  const handleClear = async () => {
    if (!(await confirm({ title: "Limpar eventos", message: i18n.t("events.confirmClear"), confirmText: "Limpar", danger: true }))) return;
    try {
      await api.delete("/events");
      setRuns([]);
      setSelectedRunId(null);
      selectedRunIdRef.current = null;
      setChain([]);
    } catch (err) {
      toastError(err);
    }
  };

  // group runs by day, preserving order
  const groups = [];
  runs.forEach(r => {
    const label = dayLabel(r.startedAt);
    let g = groups[groups.length - 1];
    if (!g || g.label !== label) {
      g = { label, items: [] };
      groups.push(g);
    }
    g.items.push(r);
  });

  const dotClass = s =>
    s === "success" ? classes.dotOk : s === "error" ? classes.dotErr : classes.dotInfo;

  return (
    <div className={classes.page}>
      <div className={classes.header}>
        <Typography className={classes.title}>{i18n.t("events.title")}</Typography>
        <span className={classes.titleCount}>{runs.length}</span>
        <div className={classes.grow} />
        <Button variant="outlined" onClick={refreshAll} disabled={loading} startIcon={<RefreshIcon />} style={{ marginRight: 8 }}>
          Atualizar
        </Button>
        <Button variant="outlined" onClick={handleClear} disabled={!runs.length}>
          {i18n.t("events.clear")}
        </Button>
      </div>

      <div className={classes.body}>
        <Paper className={classes.list} elevation={0}>
          {groups.map(g => (
            <div key={g.label}>
              <div className={classes.dayHead}>{g.label}</div>
              {g.items.map(r => (
                <div
                  key={r.runId}
                  className={`${classes.item} ${r.runId === selectedRunId ? classes.itemActive : ""}`}
                  onClick={() => selectRun(r)}
                >
                  <span className={`${classes.dot} ${dotClass(r.status)}`} />
                  <span className={classes.itemMsg}>{r.label}</span>
                  <span className={classes.itemTime} title={format(new Date(r.startedAt), "dd/MM/yyyy HH:mm:ss")}>
                    {format(new Date(r.startedAt), "HH:mm:ss")}
                  </span>
                </div>
              ))}
            </div>
          ))}
          {!loading && runs.length === 0 && (
            <div className={classes.empty}>{i18n.t("events.empty")}</div>
          )}
        </Paper>

        <Paper className={classes.detail} elevation={0}>
          {!selectedRunId ? (
            <div className={classes.empty}>Selecione uma execução para ver a cadeia (nó a nó, com entrada e saída).</div>
          ) : (
            chain.map(ev => {
              const d = ev.data || {};
              return (
                <div key={ev.id} className={classes.card}>
                  <div className={classes.cardHead}>
                    <span className={`${classes.dot} ${dotClass(ev.status)}`} />
                    <span className={classes.cardNode}>{d.node || ev.type}</span>
                    <span className={classes.cardTime} title={format(new Date(ev.createdAt), "dd/MM/yyyy HH:mm:ss")}>{format(new Date(ev.createdAt), "dd/MM HH:mm:ss")}</span>
                  </div>
                  {d.input !== undefined && (
                    <>
                      <div className={classes.metaLabel} style={{ marginTop: 6 }}>Entrada</div>
                      <JsonTree data={d.input} rootKey="in" />
                    </>
                  )}
                  {d.output !== undefined && (
                    <>
                      <div className={classes.metaLabel} style={{ marginTop: 6 }}>Saída</div>
                      <JsonTree data={d.output} rootKey="out" />
                    </>
                  )}
                  {d.prompt && (
                    <>
                      <div className={classes.metaLabel} style={{ marginTop: 6 }}>Prompt</div>
                      <div className={classes.pre}>{d.prompt}</div>
                    </>
                  )}
                  {d.transcript && (
                    <>
                      <div className={classes.metaLabel} style={{ marginTop: 6 }}>Transcrição</div>
                      <div className={classes.pre}>{d.transcript}</div>
                    </>
                  )}
                  {d.input === undefined && d.output === undefined && d.prompt === undefined && d.transcript === undefined && (
                    <JsonTree data={d} rootKey={ev.type} />
                  )}
                </div>
              );
            })
          )}
        </Paper>
      </div>
    </div>
  );
};

export default Events;
